/**
 * API client for player session endpoints.
 *
 * All methods unwrap the Rails API envelope format:
 *   response.data.data || response.data
 * so callers always receive the inner payload directly.
 * @module playerSessionService
 */

import apiClient from '../lib/apiClient'

export const playerSessionService = {
  /**
   * Fetch all completed sessions for the current user.
   * @returns {Promise<Array>} List of completed session objects
   */
  async getCompletedSessions() {
    const response = await apiClient.get('/api/player_sessions', { params: { status: 'completed' } })
    return response.data.data || response.data
  },

  /**
   * Read-only fetch of the user's in-progress session for a story — WITHOUT
   * creating one. Use this anywhere that only needs to *display* progress (e.g.
   * the story detail page). startOrResumeSession POSTs and would create a stray
   * solo session just from viewing the page; this GET never mutates.
   *
   * Team-aware: when teamContext is passed, prefer the session tagged for that
   * team_story. The index endpoint returns ALL of a user's in-progress sessions
   * (solo + team) ordered by updated_at desc, so blindly taking the most-recent
   * could surface a stray solo session's position (e.g. a leftover beat-0 solo
   * session) instead of the real team play — which renders as "back at thread 1,
   * later threads grayed out" on StoryDetail. Passing teamContext pins the read
   * to the team session.
   *
   * @param {string|number} storyId
   * @param {{teamStoryId: string|number}|null} [teamContext]
   * @returns {Promise<Object|null>} The in-progress session, or null if none
   */
  async getInProgressSessionForStory(storyId, teamContext = null) {
    const params = { status: 'in_progress' }
    if (teamContext?.teamStoryId != null) params.team_story_id = teamContext.teamStoryId
    const response = await apiClient.get(`/api/stories/${storyId}/player_sessions`, { params })
    const list = response.data.data || response.data || []
    if (!Array.isArray(list) || list.length === 0) return null
    // Prefer the team-tagged session for the active team_story; the backend
    // filter above should already scope this, but disambiguate defensively in
    // case an older backend ignores the param. Fall back to most-recent.
    if (teamContext?.teamStoryId != null) {
      const tagged = list.find(
        (s) => String(s.team_story_id) === String(teamContext.teamStoryId),
      )
      if (tagged) return tagged
    }
    return list[0]
  },

  /**
   * Create a new session or resume the most recent incomplete one for this story.
   *
   * When `teamContext` is provided, the session is tagged with team_id and
   * team_story_id so the backend can include this player in the team-story
   * aggregated analysis. Sessions started outside a team context are unchanged.
   *
   * @param {string|number} storyId
   * @param {{teamId: string|number, teamStoryId: string|number}|null} [teamContext]
   * @returns {Promise<Object>} Server session object
   */
  async startOrResumeSession(storyId, teamContext = null) {
    const body = teamContext
      ? { team_id: teamContext.teamId, team_story_id: teamContext.teamStoryId }
      : {}
    const response = await apiClient.post(`/api/stories/${storyId}/player_sessions`, body)
    return response.data.data || response.data
  },

  /**
   * Fetch a session by ID, including responses and completion state.
   * @param {string|number} sessionId
   * @returns {Promise<Object>} Full session data
   */
  async getPlayerSession(sessionId) {
    const response = await apiClient.get(`/api/player_sessions/${sessionId}`)
    return response.data.data || response.data
  },

  /**
   * Set the player's role for this session. Chosen once at the start of a
   * playthrough; the server rejects changes after that. A new session may
   * choose a different role.
   * @param {string|number} sessionId
   * @param {string} role - Role key defined in the story's player_roles
   * @returns {Promise<Object>} Updated session
   */
  async setRole(sessionId, role) {
    const response = await apiClient.patch(`/api/player_sessions/${sessionId}/set_role`, { role })
    return response.data.data || response.data
  },

  /**
   * Persist a player's response to a beat (choice selection or text input).
   * @param {string|number} sessionId
   * @param {Object} data - { beat_id, thread_id, beat_type, response_type, response_data }
   * @returns {Promise<Object>} Saved response record
   */
  async recordResponse(sessionId, data) {
    const response = await apiClient.post(`/api/player_sessions/${sessionId}/record_response`, data)
    return response.data.data || response.data
  },

  /**
   * Update the player's current position so the session can be resumed later.
   * @param {string|number} sessionId
   * @param {Object} data - { thread_id, beat_id, thread_index, beat_index }
   * @returns {Promise<Object>} Updated session
   */
  async updatePosition(sessionId, data) {
    const response = await apiClient.patch(`/api/player_sessions/${sessionId}/update_position`, data)
    return response.data.data || response.data
  },

  /**
   * Mark the session as completed (all threads finished).
   * @param {string|number} sessionId
   * @returns {Promise<Object>} Completed session
   */
  async completeSession(sessionId) {
    const response = await apiClient.post(`/api/player_sessions/${sessionId}/complete`)
    return response.data.data || response.data
  },

  /**
   * Request AI-generated feedback for the player's response to an input beat.
   * @param {string|number} sessionId
   * @param {string|number} beatId
   * @returns {Promise<Object>} Object with a .feedback string
   */
  async generateFeedback(sessionId, beatId) {
    const response = await apiClient.post(`/api/player_sessions/${sessionId}/generate_feedback`, { beat_id: beatId })
    return response.data.data || response.data
  },

  /**
   * Generate AI content for a response beat (character_response or narrative_response).
   *
   * Performs a double-unwrap: the Rails envelope gives us { beat_type, content },
   * and we return just .content so callers get the raw string (narrative) or
   * array (character dialogue lines) directly.
   * @param {string|number} sessionId
   * @param {string|number} beatId
   * @returns {Promise<string|Array>} Generated content — string for narrative, array for character
   */
  async generateBeatContent(sessionId, beatId) {
    const response = await apiClient.post(`/api/player_sessions/${sessionId}/generate_beat_content`, { beat_id: beatId })
    const result = response.data.data || response.data
    // API wraps content in { beat_type, content } — unwrap so callers get
    // the raw string (narrative_response) or array (character_response) directly
    return result.content ?? result
  },
}
